import { Text } from '@radix-ui/themes';
import { useTranslation } from 'react-i18next';
import type { TAvailableLanguageCodes } from '@hubertai/screening-question-types';
import type { TRuleEditorCommonProps } from '../types';
import LanguageCombobox from 'components/comboboxes/LanguageCombobox';
import HubertQualifiedSelect from 'components/HubertUI/EvaluationStatus/HubertQualifiedSelect';
import { EInterviewQualified } from 'components/HubertUI/EvaluationStatus/HubertQualifiedSelect/HubertQualifiedSelect.constants';
import InterviewPropertySelect from 'components/Selects/InterviewPropertySelect';
import { EInterviewProperty } from 'components/Selects/InterviewPropertySelect/InterviewPropertySelect.constants';
import type { TQuestionRuleCondition } from 'types/rules.types';

interface Props extends TRuleEditorCommonProps {
  condition: TQuestionRuleCondition;
  onChange: (patch: Partial<TQuestionRuleCondition>) => void;
  // Scopes the language picker to a fixed set (e.g. a template's own configured languages) instead
  // of every language the org supports — omit to show the full list, which is the only option that
  // makes sense for a question edited standalone, outside any template's language settings.
  allowedLanguages?: TAvailableLanguageCodes[];
}

const RuleConditionByInterviewProperty = ({ condition, isReadOnly, onChange, allowedLanguages }: Props) => {
  const { t } = useTranslation('Templates');

  return (
    <div>
      <div className="my-4 flex flex-row items-center gap-2">
        <Text className="hubert-vertical-center">{t('question.ruleConditions.propertyLabel')}</Text>
        <InterviewPropertySelect
          valuePlaceholder="Select property"
          disabled={isReadOnly}
          onSelect={(value: string) => {
            const property = value as EInterviewProperty;
            if (property === EInterviewProperty.Language) {
              onChange({ property, operator: 'IS_IN', value: [] });
            } else if (property === EInterviewProperty.Qualified) {
              onChange({ property, operator: undefined, value: EInterviewQualified.Qualified });
            } else {
              onChange({ property });
            }
          }}
          selected={condition.property ?? ''}
        />
      </div>

      {condition.property === EInterviewProperty.Qualified ? (
        <div className="hubert-vertical-center bg-primary-50 flex flex-col justify-between gap-2 rounded-md p-4">
          <Text>
            This question will <strong>only be shown</strong> if the current interview is
          </Text>
          <HubertQualifiedSelect
            disabled={isReadOnly}
            value={condition.value as string}
            onValueChange={(value: string) => onChange({ value })}
          />
        </div>
      ) : null}

      {condition.property === EInterviewProperty.Language ? (
        <div className="hubert-vertical-center bg-primary-50 flex flex-col justify-between gap-2 rounded-md p-4">
          <Text>
            This question will <strong>only be shown</strong> if interview language is any of the following
          </Text>
          <LanguageCombobox
            isMulti
            disabled={isReadOnly}
            allowLanguages={allowedLanguages}
            value={
              condition.value
                ? (Array.isArray(condition.value) ? condition.value : [condition.value]).map(String)
                : undefined
            }
            onValueChange={(value) => onChange({ value })}
          />
        </div>
      ) : null}
    </div>
  );
};

export default RuleConditionByInterviewProperty;
